// FilterChip (DESIGN §3.9.2 `filter`, interaktiv): 36 hoch, optional Avatar 28 links, Text `body`;
// gewählt `primary`/`on-primary`, sonst `pill-card` (auf Karte) bzw. `pill-page`. Als Umschalter
// (aria-pressed) oder als aktiver Filter mit × zum Entfernen.
import type { ReactNode } from 'react';
import { XIcon } from '@phosphor-icons/react';
import { cn } from '../basis/kern';

export interface FilterChipProps {
  children: ReactNode;
  /** Umschalter: gewählt? */
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  /** Aktiver Filter: × entfernt ihn (Name „Remove filter …“). */
  onRemove?: () => void;
  avatar?: ReactNode;
  surface?: 'page' | 'card';
  disabled?: boolean;
  className?: string;
}

const base = cn(
  'inline-flex h-9 shrink-0 items-center gap-2 rounded-full text-body whitespace-nowrap select-none',
  'transition-colors duration-(--bar-dur-fast) ease-standard',
);

export function FilterChip({
  children,
  selected = false,
  onSelectedChange,
  onRemove,
  avatar,
  surface = 'card',
  disabled,
  className,
}: FilterChipProps) {
  const tone = selected
    ? 'bg-primary text-on-primary'
    : surface === 'card'
      ? 'bg-pill-card text-fg hover:not-disabled:bg-pill-card-hover'
      : 'bg-pill-page text-fg hover:not-disabled:bg-pill-page-hover';
  const pad = cn(avatar ? 'pl-1' : 'pl-3.5', onRemove ? 'pr-1' : 'pr-3.5');
  const avatarNode = avatar ? (
    <span aria-hidden="true" className="inline-flex size-7 shrink-0 [&>*]:size-7">
      {avatar}
    </span>
  ) : null;

  if (onRemove) {
    const name = typeof children === 'string' ? children : 'filter';
    return (
      <span className={cn(base, tone, pad, className)}>
        {avatarNode}
        <span className="truncate">{children}</span>
        <button
          type="button"
          aria-label={`Remove filter ${name}`}
          disabled={disabled}
          onClick={onRemove}
          className="inline-flex size-7 items-center justify-center rounded-full opacity-80 hover:opacity-100 focus-visible:outline-offset-0"
        >
          <XIcon size={14} aria-hidden="true" />
        </button>
      </span>
    );
  }
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={() => onSelectedChange?.(!selected)}
      className={cn(
        base,
        tone,
        pad,
        'active:scale-[.97] disabled:cursor-not-allowed disabled:text-fg-disabled',
        className,
      )}
    >
      {avatarNode}
      <span className="truncate">{children}</span>
    </button>
  );
}
